/**
 * useSkills — fetch the visible skill list (own + team + pinned).
 *
 * Cache indefinitely (staleTime Infinity) and rely on explicit invalidation
 * when skills are created/edited/favorited. Per CLAUDE.md, arbitrary TTLs
 * are forbidden — any mutation that changes skill state should invalidate
 * `['skills']` explicitly.
 *
 * No longer takes a `blueprintKind` filter. Skills are surface-agnostic;
 * the library shows everything visible regardless of artifact context.
 * Per-blueprint relevance, if it returns, will live as a soft sort, not
 * a hard filter on the wire.
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import type { KnownSurface } from '../../../lib/skill-surfaces-client';
import { SkillListItemSchema, type SkillListItem } from '../../../types/skills';

const SkillListSchema = z.array(SkillListItemSchema);

/**
 * Fetch the visible skill list. When `surface` is supplied, the server narrows
 * to skills tagged for that surface (U2). The surface is part of the React
 * Query key so different surfaces don't collide in cache — fetching
 * `useSkills('keystone-chat')` must not overwrite the unfiltered `useSkills()`
 * cache entry (architect finding).
 */
export function useSkills(surface?: KnownSurface) {
  const { apiFetch } = useApi();
  return useQuery<SkillListItem[]>({
    queryKey: ['skills', surface ?? null],
    queryFn: async () => {
      const qs = surface ? `?surface=${encodeURIComponent(surface)}` : '';
      try {
        return await fetchJson(apiFetch, SkillListSchema, `/api/v4/skills${qs}`);
      } catch (err) {
        // A non-2xx answer degrades to an empty library, as before.
        if (err instanceof ApiProblemError && err.kind === 'http') return [];
        throw err;
      }
    },
    staleTime: Infinity,
  });
}
